import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { getConsent, saveConsent, initConsent, OPEN_EVENT } from "@/lib/consent";

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const stored = initConsent();
    if (!stored) setShowBanner(true);
    else setPrefs({ analytics: stored.analytics, marketing: stored.marketing });

    const openManage = () => {
      const s = getConsent();
      setPrefs({ analytics: s?.analytics || false, marketing: s?.marketing || false });
      setManageOpen(true);
    };
    window.addEventListener(OPEN_EVENT, openManage);
    return () => window.removeEventListener(OPEN_EVENT, openManage);
  }, []);

  const acceptAll = () => { saveConsent({ analytics: true, marketing: true }); setShowBanner(false); setManageOpen(false); };
  const rejectAll = () => { saveConsent({ analytics: false, marketing: false }); setShowBanner(false); setManageOpen(false); };
  const savePrefs = () => { saveConsent(prefs); setShowBanner(false); setManageOpen(false); };

  return (
    <>
      {showBanner && (
        <div
          data-testid="cookie-consent-banner"
          className="fixed bottom-4 left-4 z-[150] w-[min(420px,calc(100vw-2rem))] overflow-hidden rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] shadow-2xl"
        >
          <div className="p-5">
            <div className="flex items-center gap-2">
              <Cookie size={18} className="text-[#E11B22]" />
              <p className="text-[15px] font-bold text-white">We value your privacy</p>
            </div>
            <p className="mt-2 text-[13px] leading-relaxed text-white/60">
              We use essential cookies to run the site. With your consent we also use analytics cookies
              to improve AutoCareX. Non-essential tracking stays off until you choose. See our{" "}
              <Link to="/legal/cookie-policy" className="text-white underline underline-offset-2 hover:text-white/80">Cookie Policy</Link>.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={acceptAll} data-testid="cookie-accept-all" className="flex-1 rounded-[10px] bg-[#E11B22] px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#c9151b]">
                Accept all
              </button>
              <button onClick={rejectAll} data-testid="cookie-reject-all" className="flex-1 rounded-[10px] border border-[color:var(--acx-border)] bg-white/5 px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-white/10">
                Reject non-essential
              </button>
              <button onClick={() => setManageOpen(true)} data-testid="cookie-manage" className="w-full rounded-[10px] px-4 py-2 text-[12.5px] font-medium text-white/60 transition-colors hover:text-white">
                Manage preferences
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="z-[220] max-w-md border-[color:var(--acx-border)] bg-[var(--acx-card)] text-white">
          <DialogHeader><DialogTitle className="text-white">Cookie preferences</DialogTitle></DialogHeader>
          <div className="space-y-3" data-testid="cookie-preferences">
            <div className="flex items-start justify-between gap-4 rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-bg-2)] p-4">
              <div>
                <p className="text-[14px] font-semibold text-white">Essential</p>
                <p className="mt-0.5 text-[12.5px] text-white/55">Required for login, cart and security. Always on.</p>
              </div>
              <Switch checked disabled aria-label="Essential cookies always on" />
            </div>
            <div className="flex items-start justify-between gap-4 rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-bg-2)] p-4">
              <div>
                <p className="text-[14px] font-semibold text-white">Analytics</p>
                <p className="mt-0.5 text-[12.5px] text-white/55">Usage analytics &amp; session insights (PostHog, Google Analytics) to improve the site.</p>
              </div>
              <Switch checked={prefs.analytics} onCheckedChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))} data-testid="cookie-toggle-analytics" aria-label="Analytics cookies" />
            </div>
            <div className="flex items-start justify-between gap-4 rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-bg-2)] p-4">
              <div>
                <p className="text-[14px] font-semibold text-white">Marketing</p>
                <p className="mt-0.5 text-[12.5px] text-white/55">Ad measurement &amp; retargeting (Meta Pixel). Off by default.</p>
              </div>
              <Switch checked={prefs.marketing} onCheckedChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))} data-testid="cookie-toggle-marketing" aria-label="Marketing cookies" />
            </div>
          </div>
          <div className="mt-2 flex gap-2">
            <button onClick={rejectAll} className="flex-1 rounded-[10px] border border-[color:var(--acx-border)] bg-white/5 px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-white/10">Reject all</button>
            <button onClick={savePrefs} data-testid="cookie-save-prefs" className="flex-1 rounded-[10px] bg-[#E11B22] px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#c9151b]">Save preferences</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
